/**
 * Home's **Shared with you** row (TMMAPP-0159 §C 8–9): one card per virtual account shared with
 * the person, or shared by them with someone (`sharedSpaces`). Each card is a link to the virtual
 * account (`/v/{id}`) with its name, its privacy chip (`spacePrivacy`, the one rule) and its next
 * meeting when one is scheduled (the one next-meeting rule over every meeting the person is in).
 * Nothing is shown for a meeting that isn't served. The row is hidden when there are none.
 */
import { Box, Typography, useTheme } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { MeetingCalendar } from '@/api/meetings';
import { nextMeetingOf } from '@/features/meetings/nextMeeting';
import { useLocale } from '@/i18n/useLocale';
import { ToneChip } from '@/shell/PrivacyChip';
import { spacePrivacy } from '@/shell/privacy';
import { resolveTokens } from '@/theme/theme';
import { meetingDateTime } from './homeFormat';
import type { HomeSpace } from './homeScope';
import type { Read } from './useHomeData';

export function SharedSpaceCards({
  spaces,
  meetings,
  now,
  timeZone,
}: {
  spaces: readonly HomeSpace[];
  /** Every meeting the person is in, over the upcoming window. */
  meetings: Read<MeetingCalendar>;
  now: number;
  timeZone: string;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const items = meetings.status === 'ready' ? meetings.data.items : [];

  return (
    <Box
      component="ul"
      data-shared-spaces=""
      sx={{
        listStyle: 'none',
        m: 0,
        p: 0,
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 220px), 1fr))',
        gap: '14px',
      }}
    >
      {spaces.map(({ virtualAccount }) => {
        const next = nextMeetingOf(
          items.filter((entry) => entry.virtualAccountId === virtualAccount.id),
          now,
        );
        const chip = spacePrivacy(virtualAccount, t, locale);
        return (
          <Box component="li" key={virtualAccount.id} sx={{ minWidth: 0, display: 'flex' }}>
            <Box
              component={RouterLink}
              to={`/v/${virtualAccount.id}`}
              data-shared-space={virtualAccount.id}
              sx={{
                flex: 1,
                minWidth: 0,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: '10px',
                p: '20px',
                borderRadius: '20px',
                border: '1px solid',
                borderColor: tokens.line,
                bgcolor: tokens.surface,
                color: tokens.text,
                textDecoration: 'none',
                '&:hover': { borderColor: tokens.lineStrong },
                '&:focus-visible': { outline: `2px solid ${tokens.focusRing}`, outlineOffset: 2 },
              }}
            >
              <Typography
                variant="h5"
                component="span"
                sx={{ maxWidth: '100%', overflowWrap: 'anywhere' }}
              >
                {virtualAccount.name}
              </Typography>
              <ToneChip {...chip} />
              {next !== null && (
                <Typography
                  component="span"
                  data-shared-next=""
                  sx={{ fontSize: 13, lineHeight: '18px', color: tokens.text2 }}
                >
                  {t('home.shared.nextMeeting', {
                    title: next.title,
                    ...meetingDateTime(next, locale, timeZone),
                  })}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
}
